import "./App.css";

import { useState, useEffect, useRef } from "react";

import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import { useTheme } from "@mui/material/styles";

import LinkIcon from "@mui/icons-material/Link";
import Stack from "@mui/material/Stack";
import ExitToAppIcon from "@mui/icons-material/ExitToApp";
import PushPinIcon from "@mui/icons-material/PushPin";
import RestoreFromTrashIcon from "@mui/icons-material/RestoreFromTrash";
import Collapse from "@mui/material/Collapse";
import { StyledTooltip } from "./themeConfig";

import {
  labelToggleState,
  projectPickerState,
  reloadState,
} from "./JotaiAtoms";

import * as lodash from "lodash";

import { ButtonLink } from "./Image";

import { StoryLabels } from "./StoryLabels";
import { StoryCaption } from "./StoryCaption";
import { StoryDetails } from "./StoryDetails";
import { MemoMessage } from "./Thread";
import log from "./logger";

import * as u from "./utility";
import PouchDB from "pouchdb-browser";
import { useAtomValue } from "jotai";
import { useHttpPost, useExitToApp } from "./hooks";
import { TimelineChips, StoryChip } from "./TimeLineChips";
import { isPinned } from "./storydat";
import { NotifyMessage, sortMessages } from "./SendMessageBox";
import { StoryItem } from "./StoryItem";
import { MessageItem } from "./MessageItem";

type StoryCellProps = {
  item: StoryItem;
  onRestore: (item: StoryItem) => void;
  onPin: (item: StoryItem) => void;
  deleted: boolean;
  expand?: boolean;
  collapse: number;
  mode?: string;
  onUpdateLabels: (storyId: string, labels: string[]) => void;
  onLabelCreated: (labels: string[]) => void;
  setLabelDiff?: (value: React.SetStateAction<Map<string, boolean>>) => void;
  labelDiff: Map<string, boolean>;
  smoothCollapseAnimation?: boolean;
  rowSelected?: boolean;
  hideControls?: boolean;
};

export function StoryCell({
  item,
  onRestore,
  onPin,
  deleted,
  expand,
  collapse,
  mode,
  onUpdateLabels,
  onLabelCreated,
  labelDiff,
  setLabelDiff,
  smoothCollapseAnimation,
  rowSelected,
  hideControls,
}: StoryCellProps) {
  const [messages, setMessages] = useState<MessageItem[]>([]);
  const [connected, setConnected] = useState<StoryItem[]>([]);
  const [expanded, setExpanded] = useState<boolean>(false);
  const httpPost = useHttpPost();
  const project = useAtomValue(projectPickerState);
  const exitToApp = useExitToApp();
  const reloadData = useAtomValue(reloadState);
  const theme = useTheme();

  const labelToggle = useAtomValue(labelToggleState);

  let ref = useRef<{ id?: string }>({});

  // Subtle, theme-aware icon color for metadata controls
  const subtleIconColor =
    theme.palette.mode === "dark"
      ? "rgba(255, 255, 255, 0.5)"
      : "rgba(0, 0, 0, 0.4)";

  const repliesLabel = (item: StoryItem) => {
    const st_len = (item.st_len || 0) + (item.st_thc || 0);
    const num_messages = messages ? messages.length : 0;
    const thread_length = Math.max(st_len, num_messages);
    if (thread_length === 2) {
      // acccount for the thread starter
      return "1 reply";
    } else if (thread_length > 2) {
      return `${thread_length - 1} replies`;
    } else {
      return "reply";
    }
  };

  const getData = async (cached?: MessageItem[], reply?: NotifyMessage) => {
    // all args undefined -- reloading data from server
    // cached -- reloading data from cache
    // reply -- adding a new message created by the user

    if (cached && reply) {
      throw new Error(
        "invalid args: should be either one of them or none, and not the two together"
      );
    }

    try {
      const response = await httpPost("stories", {
        messages: true,
        connected: true,
        story: item.SK,
      });

      // const ts = u.getCurrentTimestamp();
      // const hardcoded = [
      //   {
      //     SK: ts,
      //     ts: ts,
      //     event_ts: ts,
      //     type: "message",
      //     st_enc: {
      //       ver: 1,
      //       cipher: "+s+mTDEUY0ljyHIZDhlS+e2N9Rfp8KuopVQx",
      //       salt: "XSnaO+WErpo8zeb+vVSP8TApusJVMZVo1r9iRO5OEOk=",
      //       iv: "kKeCgl0OBSBKEMqM",
      //     },
      //   },
      // ];

      const hardcoded: MessageItem[] = [];

      if (!messages || response.data.messages.length) {
        for (const m of response.data.messages) {
          const pp = m.st_pp;
          if (pp && typeof pp === "string") {
            m.st_pp = await u.parseJsonFromBinary(pp);
          }
        }

        const newMessages = response.data.messages.reverse().concat(hardcoded);
        const sorted = sortMessages(newMessages, true);

        const updateCache = (sorted: MessageItem[]) => {
          if (project) {
            const db = new PouchDB(project);

            const copyWithoutDummy = (e: MessageItem) => {
              let copy = { ...e };
              // TODO this is very very strange, the key is there but it would not show
              if (true || copy.old_dummy_message) {
                delete copy.old_dummy_message;
              }
              return copy;
            };
            let noDummies = sorted.map((e) => copyWithoutDummy(e));

            const key = `Thread:${item.PK}:${item.SK}`;
            const doc = { _id: key, messages: noDummies };

            db.get(key)
              .then((prev) =>
                db.put({ _rev: prev._rev, ...doc }).catch((e) => {})
              )
              .catch((e) => {
                db.put({ _id: key, message: noDummies }).catch((e) => {});
              });
          }
        };

        if (cached) {
          if (
            cached.length !== sorted.length ||
            (cached.length > 0 &&
              cached[cached.length - 1].SK !== sorted[sorted.length - 1].SK)
          ) {
            setMessages(sorted);
            updateCache(sorted);
          } else if (cached.length === sorted.length) {
            for (let i = 0; i < cached.length; ++i) {
              const rhs = cached[i] as MessageItem;
              const lhs = sorted[i] as MessageItem;
              if (
                !u.deepCompareAttrs(lhs, rhs, "st_ui", "st_hide", "st_files")
              ) {
                setMessages(sorted);
                updateCache(sorted);
                break;
              }
            }
          }
        } else {
          updateCache(sorted);
          setMessages((prev: MessageItem[]) => {
            if (!prev || !prev.length) {
              if (sorted.length > 0) {
                return sorted;
              } else {
                return prev;
              }
            } else {
              // merge messages on the screen (including those in the process of creation / uploading files)
              // with the messages obtained from the server, race conditions are possible here

              if (reply) {
                const found = prev.findIndex(
                  (e) => e.dummy && e.ts === reply.ts
                );
                if (found !== -1) {
                  let res = [...prev];
                  let dummy = res[found];
                  res[found] = { ...reply.msg, old_dummy_message: dummy };
                  // console.log(`setMessages 4: ${prev.length} -> ${res.length}`)
                  return res;
                }
              } else {
                // we get here when a user hits a refresh button or we initiate a refresh programically on a timeout

                // save those dummies as not to re-mount components, we want to preserve the (PK,SK) pairs
                // because a remounting component will issue a bunch of unnecessary http calls and cause flicker on UI
                // on the other hand we're not storing all that on the cache
                let recentlyCreated = new Map();
                prev.forEach((m, index) => {
                  if (m.old_dummy_message) {
                    recentlyCreated.set(m.SK, { message: m, index });
                  }
                });

                for (const m of sorted) {
                  if (recentlyCreated.has(m.SK)) {
                    m.old_dummy_message = recentlyCreated.get(
                      m.SK
                    ).message.old_dummy_message;
                  }
                }

                const dummies = prev.filter((e) => e.dummy);

                if (dummies.length == 0) {
                  // console.log(`setMessages 5: ${prev.length} -> ${sorted.length}`)
                  return sorted;
                } else {
                  const res = dummies.concat(sorted);
                  // console.log(`setMessages 6: ${prev.length} -> ${res.length}`)
                  return res;
                }
              }
              // console.log(`setMessages 7: noop`)
              return prev;
            }
          });
        }
      }

      for (const m of response.data.connected) {
        const pp = m.st_pp;
        if (pp && typeof pp === "string") {
          m.st_pp = await u.parseJsonFromBinary(pp);
        }
      }
      setConnected(response.data.connected);
    } catch (err) {
      log.debug(err);
    } finally {
    }
  };

  const handleExpand = (item: StoryItem) => {
    const panelExpanded = !expanded;
    setExpanded(panelExpanded);

    if (panelExpanded) {
      if (project) {
        const key = `Thread:${item.PK}:${item.SK}`;
        const db = new PouchDB(project);

        db.get(key)
          .then((doc: any) => {
            setMessages(doc.messages);
            getData(doc.messages);
          })
          .catch((err) => getData());
      }
    }
  };

  useEffect(() => {
    if (expand !== expanded) {
      handleExpand(item);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [expand]);

  useEffect(() => {
    if ("id" in ref.current && ref.current.id !== item.SK) {
      setExpanded(false);
      setMessages([]);
    }
    ref.current.id = item.SK;
  }, [item]);

  useEffect(() => {
    if (expanded) {
      handleExpand(item);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [collapse]);

  const handleLinkClick = (e: React.MouseEvent<HTMLElement>) => {
    const url = u.getStoryUrl(item, project);

    if (e.metaKey) {
      e.stopPropagation();
      window.open(url, "_blank", "noreferrer");
    } else if (!e.shiftKey) {
      navigator.clipboard.writeText(url);
    }
  };

  const handleRepliesClick = (e: React.MouseEvent<HTMLElement>) => {
    if (e.metaKey) {
      return handleLinkClick(e);
    }
    handleExpand(item);
  };

  const handleNewMessage = (msg?: NotifyMessage) => {
    getData(undefined, msg);
  };

  const handleReload = () => {
    getData();
  };

  useEffect(() => {
    // we want to limit the number of stories refreshed
    if (expanded && u.secondsSinceStoryUpdated(item) < 60 * 60 * 24 * 7) {
      getData();
    }
  }, [reloadData]);

  return (
    <Box>
      {/* Story content */}
      <MemoMessage
        item={item}
        storyKey={undefined}
        overflow={undefined}
        kind={"story"}
      />

      {/* Controls row with metadata */}
      <Box
        sx={{
          "& > :not(style) + :not(style)": {
            ml: 1,
          },
          flexDirection: "row",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          m: 0,
          mt: 0.75,
        }}
      >
        <Stack
          direction="row"
          alignItems="center"
          spacing={0.2}
          m={0}
          component="span"
        >
          {!hideControls && (
            <>
              {!(item as MessageItem).dummy && (
                <Box sx={{ whiteSpace: "nowrap" }} component="span">
                  <ButtonLink
                    text={repliesLabel(item)}
                    onclick={handleRepliesClick}
                    fontSize={undefined}
                    disabled={undefined}
                  />
                </Box>
              )}
              {true && !deleted && (
                <StyledTooltip title="Copy link to this message, or open in new tab with Meta+click">
                  <IconButton
                    size="small"
                    onClick={handleLinkClick}
                    sx={{
                      color: subtleIconColor,
                      "&:hover": {
                        color:
                          theme.palette.mode === "dark"
                            ? "rgba(255, 255, 255, 0.7)"
                            : "rgba(0, 0, 0, 0.6)",
                      },
                    }}
                  >
                    <LinkIcon fontSize="inherit" />
                  </IconButton>
                </StyledTooltip>
              )}
              {u.isSlackMessage(item) && (
                <StyledTooltip title="Open in external app">
                  <IconButton
                    size="small"
                    onClick={() => {
                      exitToApp(item);
                    }}
                    sx={{
                      color: subtleIconColor,
                      "&:hover": {
                        color:
                          theme.palette.mode === "dark"
                            ? "rgba(255, 255, 255, 0.7)"
                            : "rgba(0, 0, 0, 0.6)",
                      },
                    }}
                  >
                    <ExitToAppIcon fontSize="inherit" />
                  </IconButton>
                </StyledTooltip>
              )}
              {true && !deleted && !(item as MessageItem).dummy && (
                <StyledTooltip title="Pin topic">
                  <IconButton
                    size="small"
                    onClick={() => {
                      onPin(item);
                    }}
                    sx={{
                      color: isPinned(item)
                        ? theme.palette.primary.main
                        : subtleIconColor,
                      "&:hover": {
                        color: isPinned(item)
                          ? theme.palette.primary.main
                          : theme.palette.mode === "dark"
                          ? "rgba(255, 255, 255, 0.7)"
                          : "rgba(0, 0, 0, 0.6)",
                      },
                    }}
                  >
                    <PushPinIcon fontSize="inherit" />
                  </IconButton>
                </StyledTooltip>
              )}
              {deleted && (
                <StyledTooltip title="Restore this story">
                  <IconButton
                    size="small"
                    disabled={false}
                    onClick={() => {
                      onRestore(item);
                    }}
                    sx={{
                      color: subtleIconColor,
                      "&:hover": {
                        color:
                          theme.palette.mode === "dark"
                            ? "rgba(255, 255, 255, 0.7)"
                            : "rgba(0, 0, 0, 0.6)",
                      },
                    }}
                  >
                    <RestoreFromTrashIcon fontSize="inherit" />
                  </IconButton>
                </StyledTooltip>
              )}
            </>
          )}
          {"SK4" in item && <StoryChip item={item} rowSelected={rowSelected} />}
          <TimelineChips
            item={item}
            forRecentStories={mode === "recent" || mode === "popular"}
          />
        </Stack>
        <Stack direction="row" sx={{ width: "100%" }} justifyContent="flex-end">
          {!labelToggle && (
            <Box
              sx={{
                display: "flex",
                flexDirection: "row-reverse",
                justifyContent: "flex-start",
                width: "100%",
              }}
            >
              <StoryLabels
                item={item}
                onLabelCreated={onLabelCreated}
                onUpdateLabels={onUpdateLabels}
                minimized={true}
                diff={labelDiff}
                setDiff={setLabelDiff}
                disabled={(item as MessageItem).dummy ? true : false}
              />
            </Box>
          )}
          {!hideControls && !(item as MessageItem).dummy && (
            <StoryCaption item={item} initialValue={item.st_cap || ""} />
          )}
        </Stack>
      </Box>
      {(!smoothCollapseAnimation || expand) && expanded && (
        <Box sx={{ maxWidth: "900px", mb: 4 }}>
          <StoryDetails
            item={item}
            messages={messages}
            setMessages={setMessages}
            connected={connected}
            setConnected={setConnected}
            onNewMessage={handleNewMessage}
            onRequestReload={handleReload}
          />
        </Box>
      )}
      {smoothCollapseAnimation && !expand && (
        <Collapse in={expanded} timeout="auto">
          {expanded && (
            <Box sx={{ maxWidth: "900px", mb: 4 }}>
              <StoryDetails
                item={item}
                messages={messages}
                setMessages={setMessages}
                connected={connected}
                setConnected={setConnected}
                onNewMessage={handleNewMessage}
                onRequestReload={handleReload}
              />
            </Box>
          )}
        </Collapse>
      )}
    </Box>
  );
}
